import { useState, useCallback } from 'react';

/**
 * Standardized loading state hook.
 * Use this instead of manual `useState(true/false)` + `setLoading(true/false)`.
 *
 * @param initial - Initial loading state (default: false)
 * @returns Object with `isLoading`, `startLoading`, and `stopLoading`
 *
 * @example
 * // Instead of:
 * const [loading, setLoading] = useState(true);
 * useEffect(() => { fetchData().finally(() => setLoading(false)); }, []);
 *
 * // Use:
 * const { isLoading, stopLoading } = useLoadingState(true);
 * useEffect(() => { fetchData().finally(stopLoading); }, []);
 */
export function useLoadingState(initial = false) {
  const [isLoading, setIsLoading] = useState(initial);
  const startLoading = useCallback(() => setIsLoading(true), []);
  const stopLoading = useCallback(() => setIsLoading(false), []);
  return { isLoading, startLoading, stopLoading, setIsLoading };
}
